import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface Translations {
  [key: string]: string | Translations;
}

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  private currentLanguage = new BehaviorSubject<string>('en');
  private translations: { [lang: string]: Translations } = {};

  currentLanguage$ = this.currentLanguage.asObservable();

  constructor() {
    this.loadTranslations();
  }

  private loadTranslations(): void {
    // Імітація завантаження перекладів з JSON файлу
    this.translations = {
      en: {
        nav: {
          home: 'Home',
          about: 'About',
          services: 'Services',
          skills: 'Skills',
          experience: 'Experience',
          cases: 'Cases',
          process: 'Process',
          location: 'Location',
          testimonials: 'Testimonials',
          contact: 'Contact'
        },
        hero: {
          title: 'Lead .NET Software Engineer',
          subtitle: 'Designing scalable, high-performance solutions through clean architecture and modern .NET and cloud technologies.',
          cta: 'View My Work'
        },
        about: {
          title: 'About Me',
          description: 'I am a Senior/Lead Software Engineer who has been creating innovative solutions for businesses for over 15 years. During this time I have delivered more than 50 projects in fintech, logistics, real estate management, media and e-commerce. I have excellent skills with .NET, Node.js, JavaScript/TypeScript and modern frameworks Angular, React and Vue. With a deep understanding of architecture, SOLID principles and design patterns, I can build scalable, maintainable and secure systems.',
          description2: 'In addition to engineering expertise, I act as a Team Lead: studying business needs, defining requirements, planning architecture and leading development teams. My experience covers API creation, microservice architecture, continuous integration and deployment (CI/CD), containerization and cloud platforms (AWS, Azure, GCP). ',
          description3: 'I combine leadership and technical skills to ensure a transparent process and maximum value to the client.',
          experience: 'Years of Experience',
          projects: 'Completed Projects',
          clients: 'Happy Clients',
          coreTechnologies: 'Core Technologies'
        },
        services: {
          title: 'My Services',
          subtitle: 'What I can do for you',
          webdev: {
            title: 'Web Development',
            description: 'Creating responsive and modern websites using latest technologies'
          },
          backend: {
            title: 'Backend Development',
            description: 'Building robust server-side applications and APIs'
          },
          consulting: {
            title: 'Technical Consulting',
            description: 'Helping businesses choose the right technology stack'
          }
        },
        skills: {
          title: 'Technical Skills',
          subtitle: 'Technologies I work with',
          categories: {
            frontend: 'Frontend',
            backend: 'Backend',
            database: 'Database',
            cloud: 'Cloud & DevOps',
            tools: 'Tools & Others'
          }
        },
        cases: {
          title: 'Case Studies',
          subtitle: 'Real projects, real results',
          viewMore: 'View Details',
          techStack: 'Tech Stack',
          challenges: 'Challenges',
          solutions: 'Solutions',
          results: 'Results'
        },
        process: {
          title: 'My Process',
          subtitle: 'How I work on your project',
          steps: {
            discovery: {
              title: 'Discovery',
              description: 'Understanding your business needs and goals'
            },
            planning: {
              title: 'Planning',
              description: 'Creating detailed project roadmap and architecture'
            },
            development: {
              title: 'Development',
              description: 'Building the solution with best practices'
            },
            testing: {
              title: 'Testing',
              description: 'Comprehensive testing and quality assurance'
            },
            deployment: {
              title: 'Deployment',
              description: 'Launching your project to production'
            },
            support: {
              title: 'Support',
              description: 'Ongoing maintenance and improvements'
            }
          }
        },
        location: {
          title: 'Location & Availability',
          subtitle: 'Based in Lviv, Ukraine. Available for remote work worldwide',
          timezone: 'Timezone: UTC+2 (EET)',
          availability: 'Available for new projects',
          remote: 'Remote work preferred',
          onsite: 'On-site meetings possible in Lviv',
          advantages_title: 'Why Lviv?',
          timezone_title: 'Perfect Timezone',
          timezone_desc: 'UTC+2 timezone overlaps perfectly with European business hours',
          education_title: 'Tech Education Hub',
          education_desc: 'Home to leading technical universities and IT academies',
          cost_title: 'Cost-Effective',
          cost_desc: 'Competitive rates without compromising on quality',
          culture_title: 'Cultural Bridge',
          culture_desc: 'Perfect blend of Eastern European work ethic and Western business approach',
          eu_title: 'EU Integration',
          eu_desc: 'Strong ties with European Union markets and standards',
          tech_title: 'Tech Innovation',
          tech_desc: 'Growing tech hub with modern infrastructure and innovation centers'
        },
        testimonials: {
          title: 'Testimonials',
          subtitle: 'What clients say about working with me',
          item1_text: 'Oleksandr is extremely attentive to details. He carefully listens to requests, offers several implementation options and explains technical aspects in simple language. Working with him was very productive.',
          item1_author: 'Kurt Alexander Bakkevig, CEO MyRent',
          item2_text: 'Cannot overstate the positive impact Oleksandr had on our project. He was able to understand complex requirements and turn them into a working product quickly and without compromising on quality.',
          item2_author: 'Anonymous Client',
          item3_text: 'Professional approach, modern technologies and excellent communication. Oleksandr delivered the project on time and exceeded our expectations.',
          item3_author: 'Anonymous Client',
          button: 'More testimonials'
        },
        experience: {
          title: 'My Experience',
          subtitle: 'My professional journey and growth',
          showMore: 'Show More',
          showLess: 'Show Less',
          senior: {
            title: 'Senior Full-Stack Developer',
            company: 'Tech Solutions Inc.',
            period: '2020 - Present',
            description: 'Leading development team, architecting scalable solutions'
          },
          fullstack: {
            title: 'Full-Stack Developer',
            company: 'Digital Agency',
            period: '2018 - 2020',
            description: 'Developing web applications using React, Node.js, and PostgreSQL'
          },
          junior: {
            title: 'Junior Developer',
            company: 'StartUp Company',
            period: '2016 - 2018',
            description: 'Learning modern web development practices and technologies'
          }
        },
        contact: {
          title: 'Contact Me',
          subtitle: 'Get in touch for collaboration opportunities',
          linkedin: 'Connect with me',
          telegram: 'Message me',
          form: {
            name: 'Full Name',
            email: 'Email Address',
            message: 'Your Message',
            submit: 'Send Message',
            sending: 'Sending...'
          }
        }
      },
      uk: {
        nav: {
          home: 'Головна',
          about: 'Про мене',
          services: 'Послуги',
          skills: 'Навички',
          experience: 'Досвід',
          cases: 'Проекти',
          process: 'Процес',
          location: 'Локація',
          testimonials: 'Відгуки',
          contact: 'Контакти'
        },
        hero: {
          title: 'Lead .NET Software Engineer',
          subtitle: 'Проєктування масштабованих, високопродуктивних рішень на основі чистої архітектури, сучасних .NET технологій та хмарних сервісів.',
          cta: 'Переглянути роботи'
        },
        about: {
          title: 'Про мене',
          description: 'Я Senior/Lead Software Engineer, який створює інноваційні рішення для бізнесу вже понад 15 років. За цей час я реалізував більше ніж 50 проєктів у сферах фінтеху, логістики, управління нерухомістю, медіа та e-commerce. Я маю відмінні навички роботи з .NET, Node.js, JavaScript/TypeScript та сучасними фреймворками Angular, React і Vue. Завдяки глибокому розумінню архітектури, принципів SOLID та шаблонів проєктування я створюю масштабовані, підтримувані та безпечні системи.',
          description2: 'Окрім інженерної експертизи, я виконую роль Team Lead: вивчаю бізнес-потреби, визначаю вимоги, планую архітектуру та керую командами розробників. Мій досвід охоплює створення API, мікросервісну архітектуру, безперервну інтеграцію та деплоймент (CI/CD), контейнеризацію та хмарні платформи (AWS, Azure, GCP).',
          description3: 'Я поєдную лідерські та технічні навички, щоб забезпечити прозорий процес і максимальну цінність для клієнта.',
          experience: 'Років досвіду',
          projects: 'Завершених проектів',
          clients: 'Задоволених клієнтів',
          coreTechnologies: 'Основні технології'
        },
        services: {
          title: 'Мої послуги',
          subtitle: 'Що я можу зробити для вас',
          webdev: {
            title: 'Веб-розробка',
            description: 'Створення адаптивних та сучасних веб-сайтів з використанням найновіших технологій'
          },
          backend: {
            title: 'Backend розробка',
            description: 'Створення надійних серверних додатків та API'
          },
          consulting: {
            title: 'Технічне консультування',
            description: 'Допомога бізнесу у виборі правильного технологічного стеку'
          }
        },
        skills: {
          title: 'Технічні навички',
          subtitle: 'Технології з якими я працюю',
          categories: {
            frontend: 'Фронтенд',
            backend: 'Бекенд',
            database: 'Бази даних',
            cloud: 'Хмарні & DevOps',
            tools: 'Інструменти та інше'
          }
        },
        cases: {
          title: 'Проекти',
          subtitle: 'Реальні проекти, реальні результати',
          viewMore: 'Детальніше',
          techStack: 'Технології',
          challenges: 'Виклики',
          solutions: 'Рішення',
          results: 'Результати'
        },
        process: {
          title: 'Мій процес',
          subtitle: 'Як я працюю над вашим проектом',
          steps: {
            discovery: {
              title: 'Дослідження',
              description: 'Розуміння ваших бізнес-потреб і цілей'
            },
            planning: {
              title: 'Планування',
              description: 'Створення детального плану проекту та архітектури'
            },
            development: {
              title: 'Розробка',
              description: 'Створення рішення з найкращими практиками'
            },
            testing: {
              title: 'Тестування',
              description: 'Комплексне тестування та забезпечення якості'
            },
            deployment: {
              title: 'Деплоймент',
              description: 'Запуск вашого проекту в продакшн'
            },
            support: {
              title: 'Підтримка',
              description: 'Постійне обслуговування та покращення'
            }
          }
        },
        location: {
          title: 'Локація та доступність',
          subtitle: 'Базуюся у Львові, Україна. Доступний для віддаленої роботи по всьому світу',
          timezone: 'Часовий пояс: UTC+2 (EET)',
          availability: 'Доступний для нових проектів',
          remote: 'Віддалена робота переважна',
          onsite: 'Зустрічі на місці можливі у Львові',
          advantages_title: 'Чому Львів?',
          timezone_title: 'Ідеальний часовий пояс',
          timezone_desc: 'UTC+2 ідеально перекривається з європейськими робочими годинами',
          education_title: 'IT освітній центр',
          education_desc: 'Дім провідних технічних університетів та IT академій',
          cost_title: 'Вигідна ціна',
          cost_desc: 'Конкурентні тарифи без компромісів щодо якості',
          culture_title: 'Культурний міст',
          culture_desc: 'Ідеальне поєднання східноєвропейської трудової етики та західного бізнес-підходу',
          eu_title: 'Інтеграція з ЄС',
          eu_desc: 'Міцні зв\'язки з ринками та стандартами Європейського Союзу',
          tech_title: 'Технологічні інновації',
          tech_desc: 'Зростаючий IT-хаб з сучасною інфраструктурою та інноваційними центрами'
        },
        testimonials: {
          title: 'Відгуки клієнтів',
          subtitle: 'Що кажуть про співпрацю зі мною',
          item1_text: 'Олександр надзвичайно уважний до деталей. Він уважно вислуховує побажання, пропонує кілька варіантів реалізації та пояснює технічні аспекти простою мовою. Робота з ним була дуже продуктивною.',
          item1_author: 'Курт Александер Баккевіг, CEO MyRent',
          item2_text: 'Не можна переоцінити позитивний вплив Олександра на наш проєкт. Він зумів зрозуміти складні вимоги та перетворити їх у працюючий продукт швидко і без компромісів щодо якості.',
          item2_author: 'Анонімний клієнт',
          item3_text: 'Професійний підхід, сучасні технології та відмінна комунікація. Олександр виконав проєкт в строк і перевершив наші очікування.',
          item3_author: 'Анонімний клієнт',
          button: 'Більше відгуків'
        },
        experience: {
          title: 'Мій досвід',
          subtitle: 'Мій професійний шлях та зростання',
          showMore: 'Показати Більше',
          showLess: 'Показати Менше',
          senior: {
            title: 'Старший Full-Stack розробник',
            company: 'Tech Solutions Inc.',
            period: '2020 - Зараз',
            description: 'Керування командою розробки, архітектура масштабованих рішень'
          },
          fullstack: {
            title: 'Full-Stack розробник',
            company: 'Digital Agency',
            period: '2018 - 2020',
            description: 'Розробка веб-додатків з використанням React, Node.js та PostgreSQL'
          },
          junior: {
            title: 'Молодший розробник',
            company: 'StartUp Company',
            period: '2016 - 2018',
            description: 'Вивчення сучасних практик веб-розробки та технологій'
          }
        },
        contact: {
          title: 'Зв\'яжіться зі мною',
          subtitle: 'Напишіть для обговорення співпраці',
          linkedin: 'Підключитися зі мною',
          telegram: 'Написати мені',
          form: {
            name: 'Повне ім\'я',
            email: 'Email адреса',
            message: 'Ваше повідомлення',
            submit: 'Відправити повідомлення',
            sending: 'Відправляю...'
          }
        }
      }
    };
  }

  setLanguage(lang: string): void {
    if (this.translations[lang]) {
      this.currentLanguage.next(lang);
      // Check if localStorage is available
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem('language', lang);
      }
    }
  }

  getCurrentLanguage(): string {
    return this.currentLanguage.value;
  }

  getTranslation(key: string): string {
    const lang = this.getCurrentLanguage();
    const keys = key.split('.');
    let result: any = this.translations[lang];

    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        return key; // Return key if translation not found
      }
    }

    return typeof result === 'string' ? result : key;
  }

  // Імітація API виклику для завантаження перекладів
  loadTranslationsFromAPI(): Observable<any> {
    return of(this.translations).pipe(delay(500));
  }

  // Initialize language from localStorage or browser preference
  initializeLanguage(): void {
    // Check if we're in the browser (not SSR)
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const savedLang = localStorage.getItem('language');
      const browserLang = navigator.language.split('-')[0];
      const defaultLang = savedLang || (this.translations[browserLang] ? browserLang : 'en');

      this.setLanguage(defaultLang);
    } else {
      // Default to English for SSR
      this.setLanguage('en');
    }
  }
}
